import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "MastercardnReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    mastercard_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        inquiry_summary: false,
        merchant_details: false,
        principal_information: false,
        match_analysis: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    // Pagination for merchants - split into groups that fit on one sheet
    paginatedMerchants() {
      const itemsPerPage = 2; // Adjust based on how many fit comfortably on one sheet
      const pages = [];
      for (let i = 0; i < this.terminated_merchants.length; i += itemsPerPage) {
        pages.push(this.terminated_merchants.slice(i, i + itemsPerPage));
      }
      return pages;
    },
    
    // Pagination for principals
    paginatedPrincipals() {
      const itemsPerPage = 15; // Adjust based on table row height
      const pages = [];
      for (let i = 0; i < this.all_principals.length; i += itemsPerPage) {
        pages.push(this.all_principals.slice(i, i + itemsPerPage));
      }
      return pages;
    },
    
    // Pagination for match analysis
    paginatedMatchAnalysis() {
      const itemsPerPage = 15; // Adjust based on table row height
      const pages = [];
      for (let i = 0; i < this.terminated_merchants.length; i += itemsPerPage) {
        pages.push(this.terminated_merchants.slice(i, i + itemsPerPage));
      }
      return pages;
    },
    
    // Pagination for termination details
    paginatedTerminationDetails() {
      const itemsPerPage = 15; // Adjust based on table row height
      const pages = [];
      for (let i = 0; i < this.terminated_merchants.length; i += itemsPerPage) {
        pages.push(this.terminated_merchants.slice(i, i + itemsPerPage));
      }
      return pages;
    },
    has_selected_tables() {
      return Object.values(this.export_options).some((option) => option);
    },
    safe_mastercard_data() {
      return this.mastercard_data?.terminationInquiryResponse || {};
    },

    // Basic Inquiry Information
    inquiry_reference() {
      return this.safe_mastercard_data.ref || "N/A";
    },

    page_offset() {
      return this.safe_mastercard_data.pageOffset || "0";
    },

    transaction_reference_number() {
      return (
        this.safe_mastercard_data.transactionReferenceNumber || "Not Available"
      );
    },

    total_merchant_matches() {
      const matches = this.safe_mastercard_data.possibleMerchantMatches;
      return matches && matches.length > 0
        ? matches[0].totalMerchantMatches
        : 0;
    },

    terminated_merchants() {
      const matches = this.safe_mastercard_data.possibleMerchantMatches;
      return matches && matches.length > 0
        ? matches[0].terminatedMerchants || []
        : [];
    },

    terminated_merchants_count() {
      return this.terminated_merchants.length;
    },

    // Risk Assessment
    risk_assessment() {
      if (this.terminated_merchants_count === 0) return "Low Risk";
      if (this.terminated_merchants_count <= 2) return "Medium Risk";
      return "High Risk";
    },

    // Principal Information
    all_principals() {
      const principals = [];
      this.terminated_merchants.forEach((merchant) => {
        if (merchant.merchant.principals) {
          merchant.merchant.principals.forEach((principal) => {
            principals.push({
              ...principal,
              merchant_name: merchant.merchant.name,
              merchant_id: merchant.merchant.merchantId,
            });
          });
        }
      });
      return principals;
    },

    has_principal_data() {
      return this.all_principals.length > 0;
    },

    current_date() {
      return new Date().toISOString();
    },

    formatted_current_date() {
      return this.formatDate(this.current_date);
    },

    // Report metadata
    report_reference() {
      return this.inquiry_reference;
    },

    // Badge classes and icons for matches status
    matches_status_badge_class() {
      if (this.total_merchant_matches === 0) return "status-good";
      if (this.total_merchant_matches <= 2) return "status-neutral";
      return "status-bad";
    },

    matches_status_icon() {
      if (this.total_merchant_matches === 0) return "fas fa-check-circle";
      if (this.total_merchant_matches <= 2) return "fas fa-exclamation-circle";
      return "fas fa-times-circle";
    },

    // Badge classes and icons for risk level
    risk_level_badge_class() {
      if (this.risk_assessment === "Low Risk") return "status-good";
      if (this.risk_assessment === "Medium Risk") return "status-neutral";
      return "status-bad";
    },

    risk_level_icon() {
      if (this.risk_assessment === "Low Risk") return "fas fa-shield-alt";
      if (this.risk_assessment === "Medium Risk")
        return "fas fa-exclamation-triangle";
      return "fas fa-exclamation-circle";
    },

    modal_instance() {
      var modal = new window.bootstrap.Modal(
        document.getElementById(`export-modal-${this.report_reference}`)
      );
      return modal;
    },
  },

  methods: {
    close_modal() {
      this.modal_instance.hide();
    },

    // Utility methods
    formatDate(date) {
      return format_date(date);
    },

    format_merchant_address(address) {
      if (!address) return "Not Available";

      const parts = [
        address.addressLineOne,
        address.addressLineTwo,
        address.city,
        address.province,
        address.countrySubdivision,
        address.country,
        address.postalCode,
      ].filter((part) => part && part.trim() !== "");

      return parts.join(", ") || "Not Available";
    },

    format_principal_address(address) {
      if (!address) return "Not Available";

      const parts = [
        address.addressLineOne,
        address.addressLineTwo,
        address.city,
        address.province,
        address.countrySubdivision,
        address.country,
        address.postalCode,
      ].filter((part) => part && part.trim() !== "");

      return parts.join(", ") || "Not Available";
    },

    format_principal_name(principal) {
      const parts = [
        principal.firstName,
        principal.middleInitial,
        principal.lastName,
      ].filter((part) => part && part.trim() !== "");

      return parts.join(" ") || "Not Available";
    },

    format_masked_date(date) {
      if (!date || date === "*****") return "Masked";
      return this.formatDate(date);
    },

    format_match_score(matchCode) {
      const matchDescriptions = {
        M00: "No Match",
        M01: "Exact Match",
        M02: "Close Match",
        M03: "Partial Match",
      };

      return matchDescriptions[matchCode] || matchCode || "N/A";
    },

    get_match_badge_class(matchCode) {
      if (matchCode === "M01") return "bg-danger"; // Exact match is high risk
      if (matchCode === "M02") return "bg-warning"; // Close match is medium risk
      if (matchCode === "M03") return "bg-warning"; // Partial match is medium risk
      return "bg-success"; // No match is good
    },

    get_termination_reason_badge_class(reasonCode) {
      // High risk reasons
      const highRiskCodes = ["03", "13"]; // Laundering, Illegal Transactions
      // Medium risk reasons
      const mediumRiskCodes = ["01"]; // Account Data Compromise

      if (highRiskCodes.includes(reasonCode)) return "bg-danger";
      if (mediumRiskCodes.includes(reasonCode)) return "bg-warning";
      return "bg-secondary";
    },

    get_termination_reason_icon(reasonCode) {
      const highRiskCodes = ["03", "13"];
      const mediumRiskCodes = ["01"];

      if (highRiskCodes.includes(reasonCode)) return "fas fa-ban";
      if (mediumRiskCodes.includes(reasonCode))
        return "fas fa-exclamation-triangle";
      return "fas fa-info-circle";
    },

    get_overall_risk_level(merchant) {
      const matches = merchant.merchantMatch;
      const exactMatches = Object.values(matches).filter(
        (match) => match === "M01"
      ).length;
      const closeMatches = Object.values(matches).filter(
        (match) => match === "M02"
      ).length;

      if (exactMatches >= 3) return "High Risk";
      if (exactMatches >= 1 || closeMatches >= 3) return "Medium Risk";
      return "Low Risk";
    },

    get_overall_risk_badge_class(merchant) {
      const riskLevel = this.get_overall_risk_level(merchant);
      if (riskLevel === "High Risk") return "status-bad";
      if (riskLevel === "Medium Risk") return "status-neutral";
      return "status-good";
    },

    get_overall_risk_icon(merchant) {
      const riskLevel = this.get_overall_risk_level(merchant);
      if (riskLevel === "High Risk") return "fas fa-exclamation-circle";
      if (riskLevel === "Medium Risk") return "fas fa-exclamation-triangle";
      return "fas fa-shield-alt";
    },

    // Export functionality
    show_export_modal() {
      this.export_options.inquiry_summary = true;
      this.export_options.merchant_details = true;
      this.export_options.principal_information = true;
      this.export_options.match_analysis = true;
      this.export_options.raw_json = false;
      this.modal_instance.show();
    },

    async load_export_libraries() {
      if (!this.libraries_loaded.papa_parse) {
        await this.load_script(
          "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.0/papaparse.min.js"
        );
        this.libraries_loaded.papa_parse = true;
      }

      if (!this.libraries_loaded.sheetjs) {
        await this.load_script(
          "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.17.0/xlsx.full.min.js"
        );
        this.libraries_loaded.sheetjs = true;
      }
    },

    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async export_data() {
      await this.load_export_libraries();

      if (this.export_format === "csv") {
        await this.export_csv();
      } else if (this.export_format === "excel") {
        await this.export_excel();
      } else if (this.export_format === "json") {
        await this.export_json();
      }

      // Close modal
      this.close_modal();
    },

    // Prepare inquiry summary data
    prepare_inquiry_summary_data() {
      return [
        {
          "Report Reference": this.report_reference,
          "Inquiry Reference": this.inquiry_reference,
          "Page Offset": this.page_offset,
          "Transaction Reference": this.transaction_reference_number,
          "Total Merchant Matches": this.total_merchant_matches,
          "Terminated Merchants Count": this.terminated_merchants_count,
          "Risk Assessment": this.risk_assessment,
          "Generated Date": this.formatted_current_date,
        },
      ];
    },

    // Prepare merchant details data
    prepare_merchant_details_data() {
      return this.terminated_merchants.map((merchant) => ({
        "Merchant Name": merchant.merchant.name,
        "DBA Name": merchant.merchant.doingBusinessAsName,
        "Merchant ID": merchant.merchant.merchantId,
        "Sub Merchant ID": merchant.merchant.subMerchantId || "N/A",
        "Phone Number": merchant.merchant.phoneNumber,
        "Alt Phone Number": merchant.merchant.altPhoneNumber,
        Address: this.format_merchant_address(merchant.merchant.address),
        City: merchant.merchant.address?.city || "N/A",
        Country: merchant.merchant.address?.country || "N/A",
        "Postal Code": merchant.merchant.address?.postalCode || "N/A",
        "Merchant Category": merchant.merchant.merchantCategory,
        "National Tax ID": merchant.merchant.nationalTaxId || "N/A",
        "Date Opened": this.formatDate(merchant.merchant.dateOpened),
        "Date Closed": this.formatDate(merchant.merchant.dateClosed),
        "Reason Code": merchant.merchant.reasonCode,
        "Reason Description": merchant.merchant.reasonCodeDesc,
        "Added By Acquirer ID": merchant.merchant.addedByAcquirerId,
        "Created Date": this.formatDate(merchant.merchant.createdDate),
        Comments: merchant.merchant.comments || "N/A",
        "Merchant Reference Number": merchant.merchant.merchRefNum,
        "Overall Risk Level": this.get_overall_risk_level(merchant),
      }));
    },

    // Prepare principal information data
    prepare_principal_information_data() {
      return this.all_principals.map((principal) => ({
        "Merchant Name": principal.merchant_name,
        "Merchant ID": principal.merchant_id,
        "Principal Name": this.format_principal_name(principal),
        "First Name": principal.firstName,
        "Middle Initial": principal.middleInitial || "N/A",
        "Last Name": principal.lastName,
        Address: this.format_principal_address(principal.address),
        City: principal.address?.city || "N/A",
        Country: principal.address?.country || "N/A",
        "Phone Number": principal.phoneNumber || "N/A",
        "Alt Phone Number": principal.altPhoneNumber || "N/A",
        Email: principal.email || "N/A",
        "Date of Birth": this.format_masked_date(principal.dateOfBirth),
        "National ID": principal.nationalId || "N/A",
        "Drivers License Number": principal.driversLicense?.number || "N/A",
      }));
    },

    // Prepare match analysis data
    prepare_match_analysis_data() {
      return this.terminated_merchants.map((merchant) => ({
        "Merchant Name": merchant.merchant.name,
        "Merchant ID": merchant.merchant.merchantId,
        "Name Match": this.format_match_score(merchant.merchantMatch.name),
        "DBA Match": this.format_match_score(
          merchant.merchantMatch.doingBusinessAsName
        ),
        "Phone Match": this.format_match_score(
          merchant.merchantMatch.phoneNumber
        ),
        "Address Match": this.format_match_score(
          merchant.merchantMatch.address
        ),
        "Alt Phone Match": this.format_match_score(
          merchant.merchantMatch.altPhoneNumber
        ),
        "Tax ID Match": this.format_match_score(
          merchant.merchantMatch.nationalTaxId
        ),
        "Country Tax ID Match": this.format_match_score(
          merchant.merchantMatch.countrySubdivisionTaxId
        ),
        "Overall Risk Level": this.get_overall_risk_level(merchant),
        "Termination Reason": merchant.merchant.reasonCodeDesc,
      }));
    },

    // Prepare raw JSON data
    prepare_raw_json_data() {
      return {
        report_metadata: {
          report_reference: this.report_reference,
          inquiry_reference: this.inquiry_reference,
          generated_date: this.formatted_current_date,
          export_date: new Date().toISOString(),
          report_type: "Mastercard Merchant Termination Report",
        },
        raw_mastercard_data: this.mastercard_data,
      };
    },

    // Export methods
    async export_csv() {
      const Papa = window.Papa;
      const current_date = new Date().toISOString().split("T")[0];

      // Map section keys to their data preparation methods and file suffixes
      const sectionMap = [
        {
          key: "inquiry_summary",
          label: "Inquiry_Summary",
          prepare: this.prepare_inquiry_summary_data,
        },
        {
          key: "merchant_details",
          label: "Merchant_Details",
          prepare: this.prepare_merchant_details_data,
        },
        {
          key: "principal_information",
          label: "Principal_Information",
          prepare: this.prepare_principal_information_data,
        },
        {
          key: "match_analysis",
          label: "Match_Analysis",
          prepare: this.prepare_match_analysis_data,
        },
      ];

      // For each selected section, generate and download a CSV
      for (const section of sectionMap) {
        if (this.export_options[section.key]) {
          const data = section.prepare.call(this);
          if (data && data.length > 0) {
            const csv = Papa.unparse(data);
            const filename = `Mastercard_Termination_Report_${section.label}_${current_date}.csv`;
            this.download_file(csv, filename, "text/csv");
          }
        }
      }
    },

    async export_excel() {
      const XLSX = window.XLSX;
      const current_date = new Date().toISOString().split("T")[0];
      const workbook = XLSX.utils.book_new();

      if (this.export_options.inquiry_summary) {
        const inquiry_data = this.prepare_inquiry_summary_data();
        const worksheet = XLSX.utils.json_to_sheet(inquiry_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Inquiry Summary");
      }

      if (this.export_options.merchant_details) {
        const merchant_data = this.prepare_merchant_details_data();
        const worksheet = XLSX.utils.json_to_sheet(merchant_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Merchant Details");
      }

      if (this.export_options.principal_information) {
        const principal_data = this.prepare_principal_information_data();
        const worksheet = XLSX.utils.json_to_sheet(principal_data);
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          "Principal Information"
        );
      }

      if (this.export_options.match_analysis) {
        const match_data = this.prepare_match_analysis_data();
        const worksheet = XLSX.utils.json_to_sheet(match_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Match Analysis");
      }

      if (this.export_options.raw_json) {
        const json_data = this.prepare_raw_json_data();
        // For Excel, create a simple table with the JSON as text
        const json_table = [
          {
            "JSON Data": JSON.stringify(json_data, null, 2),
          },
        ];
        const worksheet = XLSX.utils.json_to_sheet(json_table);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Raw JSON Data");
      }

      const filename = `Mastercard_Termination_Report_${current_date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },

    async export_json() {
      const current_date = new Date().toISOString().split("T")[0];
      const export_data = {};

      if (this.export_options.inquiry_summary) {
        export_data.inquiry_summary = this.prepare_inquiry_summary_data()[0];
      }

      if (this.export_options.merchant_details) {
        export_data.merchant_details = this.prepare_merchant_details_data();
      }

      if (this.export_options.principal_information) {
        export_data.principal_information =
          this.prepare_principal_information_data();
      }

      if (this.export_options.match_analysis) {
        export_data.match_analysis = this.prepare_match_analysis_data();
      }

      if (this.export_options.raw_json) {
        export_data.raw_data = this.prepare_raw_json_data();
      }

      const json = JSON.stringify(export_data, null, 2);
      const filename = `Mastercard_Termination_Report_${current_date}.json`;
      this.download_file(json, filename, "application/json");
    },

    download_file(content, filename, content_type) {
      const blob = new Blob([content], { type: content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
};
